import { LoadFailed, QueryLoading } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { requireAccess } from '../features/auth/routeAccess';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Panel, PanelHeader, Select } from '@promanage/ui';
import {
  formatPeriod,
  isPeriodStart,
  nzDate,
  periodEndsOn,
  periodStartFor,
  previousPeriodStart,
  canReadTrustAccount,
} from '@promanage/core';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { ReconciliationReport } from '../features/reports/ReconciliationReport';
import { recentMonths } from '../features/reports/months';
import { letterheadLogo } from '../features/branding/letterhead';
import { trpc } from '../lib/trpc';

/**
 * Which month the page is showing, as it lives in the URL.
 *
 * SHAPE-CHECKED ONLY, like the statement picker's and the audit report's. A month this agency has
 * no books for is answered by a report saying nothing posted, which is a truthful document rather
 * than an error, so the only thing refused here is a string that is not a month at all.
 *
 * NO ZOD, and that is a bundle decision rather than a preference: `validateSearch` runs during
 * ROUTING, so whatever it imports lands in the eager bundle on every page of the app.
 * `isPeriodStart` is a regex test in core that is already there. See `statementSearch.ts`.
 */
function parseSearch(search: Record<string, unknown>): { period?: string } {
  return typeof search.period === 'string' && isPeriodStart(search.period)
    ? { period: search.period }
    : {};
}

export const Route = createFileRoute('/reports/reconciliation')({
  // Every figure on it is the trust account's own — `reports.reconciliationMonth` refuses anybody else.
  beforeLoad: requireAccess(canReadTrustAccount),
  validateSearch: parseSearch,
  component: ReconciliationReportPage,
});

/** How many months back the picker offers. Two years of month ends is more than anybody reopens. */
const MONTHS_OFFERED = 24;

/**
 * The monthly trust reconciliation report.
 *
 * WHAT IT IS FOR. This is the document the agency is asked for — by their auditor, by a
 * franchisor, by their own accountant at year end — and until now every figure on it existed in
 * the database with no way to hand it to anybody. §7's month close records the sign-off; this
 * produces the thing that gets signed.
 *
 * THE MONTH IS IN THE URL, for the reason the audit report's filter and the statement's owner
 * are: a report is something an operator sends to a colleague to check, reloads after fixing a
 * day, and comes back to when somebody asks about it in March. Component state survives none of
 * the three.
 *
 * TWO EXPORTS, AND THEY ARE NOT ALTERNATIVES. The PDF is the report — read, signed and filed. The
 * CSV is the postings behind it — opened in a spreadsheet, sorted, subtotalled and tied back. An
 * auditor asks for both and they answer different questions, which is why the CSV is not simply
 * an appendix to the PDF.
 */
function ReconciliationReportPage() {
  const selection = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  /*
   * Which month is "now" — a NEW ZEALAND day, resolved from the browser's instant.
   *
   * Then the month BEFORE it, which is the default and is the whole point: nobody reconciles the
   * month they are in. Opening on the current month would show a half-finished document with
   * every account "not reconciled to the last day", every month, to everybody.
   */
  const today = nzDate(new Date());
  const period = selection.period ?? previousPeriodStart(periodStartFor(today));

  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  const query = useQuery(
    trpc.reports.reconciliationMonth.queryOptions(
      { period },
      { meta: { errorTitle: 'We could not produce that reconciliation' }, staleTime: 30_000 },
    ),
  );

  /*
   * §5's last control, fetched alongside: "any bank account edit appears on the next
   * reconciliation report as a highlighted event". Its own procedure rather than a field on the
   * report, because it already existed and answers a question about payees rather than about the
   * ledger — folding it into the report's query would have meant two reasons for one read.
   */
  const payeeChanges = useQuery(
    trpc.reconciliation.bankAccountChanges.queryOptions({ month: period }, { staleTime: 60_000 }),
  );

  const report = query.data;
  const months = recentMonths(periodStartFor(today), MONTHS_OFFERED);

  return (
    <ReportWorkspace
      title="Trust reconciliation"
      subtitle={`${formatPeriod(period)} — as at ${periodEndsOn(period)}`}
      actions={
        <>
          <ExportPdfButton
            label="Transactions (CSV)"
            failureTitle="Could not build the transaction export"
            disabled={report === undefined}
            onExport={async () => {
              if (report === undefined) return;

              /*
               * BOTH THE ROWS AND THE WRITER ARE FETCHED BY THE CLICK.
               *
               * A month of postings is thousands of rows, and this page is opened far more often
               * to read the reconciliation than to export the ledger behind it — so the rows are
               * pulled with `fetchQuery` at the moment somebody asks rather than by a `useQuery`
               * that would run on every page view. The writer is a lazy chunk for the reason
               * every export module here is (DESIGN_SYSTEM.md §8).
               */
              const [module, data] = await Promise.all([
                import('../features/reports/transactionExport'),
                queryClient.fetchQuery(
                  trpc.reports.transactionExport.queryOptions({
                    from: report.period,
                    to: report.endsOn,
                  }),
                ),
              ]);

              module.saveTransactionExport(
                module.transactionExportFileName(data.from, data.to),
                module.transactionExportCsv(data),
              );
            }}
          />

          <ExportPdfButton
            label="Download PDF"
            disabled={report === undefined}
            onExport={async () => {
              if (report === undefined) return;
              const { exportReconciliationPdf } =
                await import('../features/reports/reconciliationPdf');
              exportReconciliationPdf({
                report,
                payeeChanges: payeeChanges.data,
                orgName,
                logo: await letterheadLogo(),
                generatedAt: new Date(),
              });
            }}
          />

          <Button size="sm" onClick={() => window.print()} disabled={report === undefined}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="One month, as at its last day — the unit an agency reconciles, locks and signs."
          />
          <div className="px-5 pb-5">
            <Field label="Month" className="max-w-64">
              {(props) => (
                <Select
                  {...props}
                  value={period}
                  onChange={(event) => {
                    // `replace`, so the back button leaves the screen rather than walking back
                    // through every month somebody stepped past.
                    void navigate({ search: { period: event.target.value }, replace: true });
                  }}
                >
                  {months.map((month) => (
                    <option key={month} value={month}>
                      {formatPeriod(month)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {query.isError ? (
          <LoadFailed retry={() => void query.refetch()} retrying={query.isFetching} />
        ) : null}

        {query.isPending ? (
          <QueryLoading />
        ) : report === undefined ? null : (
          <ReconciliationReport
            report={report}
            payeeChanges={payeeChanges.data}
            orgName={orgName}
            // Read at render, not pinned: the produced-at line has to be the moment this copy came
            // off the system, and a report left open overnight is one whose figures may have moved.
            generatedAt={new Date()}
          />
        )}
      </div>
    </ReportWorkspace>
  );
}
